import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  User, 
  Mail, 
  Phone, 
  CreditCard,
  CheckCircle,
  ArrowRight,
  MapPin,
  Monitor
} from 'lucide-react';

const BookingSystem = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    service: '',
    language: '',
    instructor: '',
    date: '',
    time: '',
    type: '',
    studentInfo: {
      name: '',
      age: '',
      email: '',
      phone: '',
      parentName: '',
      parentEmail: '',
      experience: ''
    },
    paymentMethod: ''
  });

  const services = [
    { 
      id: 'consultation', 
      name: 'Free Consultation', 
      duration: '30 min', 
      price: 'Free',
      description: 'Meet with our language experts to discuss your learning goals'
    },
    { 
      id: 'assessment', 
      name: 'Language Assessment', 
      duration: '45 min', 
      price: '$25',
      description: 'Comprehensive evaluation to determine your current level'
    },
    { 
      id: 'trial-class', 
      name: 'Trial Class', 
      duration: '60 min', 
      price: '$35',
      description: 'Experience our teaching methodology with a sample lesson'
    },
    { 
      id: 'private-lesson', 
      name: 'Private Lesson', 
      duration: '60 min', 
      price: '$75',
      description: 'One-on-one personalized instruction'
    }
  ];

  const languages = ['English', 'French', 'Italian', 'Spanish', 'German', 'Portuguese'];
  
  const instructors = [
    { id: 1, name: 'Sarah Johnson', specialty: 'English & IELTS', rating: 4.9, experience: '8 years' },
    { id: 2, name: 'Dr. Marie Dubois', specialty: 'French & DELF/DALF', rating: 4.9, experience: '12 years' },
    { id: 3, name: 'Carlos Rodriguez', specialty: 'Spanish & D.E.L.E.', rating: 4.8, experience: '10 years' },
    { id: 4, name: 'Hans Mueller', specialty: 'German & TestDaF', rating: 4.9, experience: '9 years' }
  ];

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM',
    '5:00 PM', '6:00 PM', '7:00 PM'
  ];

  const steps = [
    { number: 1, title: 'Select Service', icon: Calendar },
    { number: 2, title: 'Choose Details', icon: User },
    { number: 3, title: 'Schedule', icon: Clock },
    { number: 4, title: 'Student Info', icon: User },
    { number: 5, title: 'Payment', icon: CreditCard },
    { number: 6, title: 'Confirmation', icon: CheckCircle }
  ];

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBookingData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBookingData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-white py-12">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl lg:text-5xl font-bold font-heading text-primary-500 mb-4">
            Book Your Language Learning Session
          </h1>
          <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
            Schedule your consultation, assessment, or lesson with our expert instructors
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-4 mb-8 overflow-x-auto">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <motion.div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
                    currentStep >= step.number
                      ? 'bg-accent-500 text-white'
                      : 'bg-neutral-200 text-neutral-500'
                  }`}
                  whileHover={{ scale: 1.1 }}
                >
                  {currentStep > step.number ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-6 h-6" />
                  )}
                </motion.div>
                <div className="ml-3 hidden md:block">
                  <div className={`text-sm font-medium ${
                    currentStep >= step.number ? 'text-primary-500' : 'text-neutral-400'
                  }`}>
                    Step {step.number}
                  </div>
                  <div className={`text-xs ${
                    currentStep >= step.number ? 'text-neutral-600' : 'text-neutral-400'
                  }`}>
                    {step.title}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-8 h-1 mx-4 ${
                    currentStep > step.number ? 'bg-accent-500' : 'bg-neutral-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Booking Form */}
        <div className="max-w-4xl mx-auto">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl shadow-xl p-8 lg:p-12"
          >
            {/* Step 1: Select Service */}
            {currentStep === 1 && (
              <div>
                <h2 className="text-3xl font-bold text-primary-500 mb-8">Select Your Service</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {services.map((service) => (
                    <motion.div
                      key={service.id}
                      onClick={() => handleInputChange('service', service.id)}
                      className={`p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                        bookingData.service === service.id
                          ? 'border-accent-500 bg-accent-50'
                          : 'border-neutral-200 hover:border-accent-300'
                      }`}
                      whileHover={{ y: -5 }}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-primary-500">{service.name}</h3>
                        <span className="text-2xl font-bold text-accent-500">{service.price}</span>
                      </div>
                      <p className="text-neutral-600 mb-4">{service.description}</p>
                      <div className="flex items-center space-x-2 text-sm text-neutral-500">
                        <Clock className="w-4 h-4" />
                        <span>{service.duration}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Choose Details */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-3xl font-bold text-primary-500 mb-8">Choose Language & Instructor</h2>
                
                {/* Language Selection */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4">Language</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {languages.map((language) => (
                      <motion.button
                        key={language}
                        onClick={() => handleInputChange('language', language)}
                        className={`p-4 rounded-xl border-2 font-medium transition-all ${
                          bookingData.language === language
                            ? 'border-accent-500 bg-accent-50 text-accent-600'
                            : 'border-neutral-200 hover:border-accent-300'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {language}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Lesson Type */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4">Lesson Type</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { id: 'in-person', name: 'In-Person', icon: MapPin, description: 'At our school location' },
                      { id: 'online', name: 'Online', icon: Monitor, description: 'Virtual classroom' }
                    ].map((type) => (
                      <motion.div
                        key={type.id}
                        onClick={() => handleInputChange('type', type.id)}
                        className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                          bookingData.type === type.id
                            ? 'border-accent-500 bg-accent-50'
                            : 'border-neutral-200 hover:border-accent-300'
                        }`}
                        whileHover={{ y: -2 }}
                      >
                        <div className="flex items-center space-x-3 mb-2">
                          <type.icon className="w-6 h-6 text-accent-500" />
                          <h4 className="text-lg font-semibold text-primary-500">{type.name}</h4>
                        </div>
                        <p className="text-neutral-600">{type.description}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Instructor Selection */}
                <div>
                  <h3 className="text-xl font-semibold text-primary-500 mb-4">Preferred Instructor</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {instructors.map((instructor) => (
                      <motion.div
                        key={instructor.id}
                        onClick={() => handleInputChange('instructor', instructor.id)}
                        className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                          bookingData.instructor === instructor.id
                            ? 'border-accent-500 bg-accent-50'
                            : 'border-neutral-200 hover:border-accent-300'
                        }`}
                        whileHover={{ y: -2 }}
                      >
                        <h4 className="text-lg font-semibold text-primary-500 mb-2">{instructor.name}</h4>
                        <p className="text-neutral-600 mb-2">{instructor.specialty}</p>
                        <div className="flex justify-between text-sm text-neutral-500">
                          <span>⭐ {instructor.rating}</span>
                          <span>{instructor.experience}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Schedule */}
            {currentStep === 3 && (
              <div>
                <h2 className="text-3xl font-bold text-primary-500 mb-8">Select Date & Time</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Date Selection */}
                  <div>
                    <h3 className="text-xl font-semibold text-primary-500 mb-4">Choose Date</h3>
                    <input
                      type="date"
                      value={bookingData.date}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full p-4 border-2 border-neutral-200 rounded-xl focus:border-accent-500 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Time Selection */}
                  <div>
                    <h3 className="text-xl font-semibold text-primary-500 mb-4">Available Times</h3>
                    <div className="grid grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                      {timeSlots.map((time) => (
                        <motion.button
                          key={time}
                          onClick={() => handleInputChange('time', time)}
                          className={`p-3 rounded-lg border-2 font-medium transition-all ${
                            bookingData.time === time
                              ? 'border-accent-500 bg-accent-50 text-accent-600'
                              : 'border-neutral-200 hover:border-accent-300'
                          }`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {time}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-12">
              {currentStep > 1 && (
                <motion.button
                  onClick={prevStep}
                  className="bg-neutral-200 hover:bg-neutral-300 text-neutral-700 font-semibold px-8 py-4 rounded-2xl transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Previous
                </motion.button>
              )}
              
              <motion.button
                onClick={nextStep}
                disabled={currentStep === 1 && !bookingData.service}
                className="bg-accent-500 hover:bg-accent-600 disabled:bg-neutral-300 text-white font-semibold px-8 py-4 rounded-2xl flex items-center space-x-2 transition-colors ml-auto"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>{currentStep === 6 ? 'Complete Booking' : 'Next Step'}</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BookingSystem;